import { AnyAction } from 'redux';
import { DocumentStatus } from '@synergy/models';

import { Account } from '../../../../../common/models/account';
import {
    INVOICE_DETAIL_CANCELED,
    INVOICE_DETAIL_COMPLETE,
    INVOICE_DETAIL_IS_CREDIT_CHANGED,
    INVOICE_DETAIL_RESET,
} from '../invoice-detail-actions';
import { INVOICE_DETAIL_OVERVIEW_RECEIVE, INVOICE_DETAIL_OVERVIEW_SUBMIT } from './invoice-detail-overview-actions';
import { INVOICE_DETAIL_POSITIONS_RECEIVE } from '../positions/invoice-detail-positions-actions';
import { Invoice } from '../../../models/invoice';
import { OverviewFormValues } from '../../models/overview-form-values';
import { StepData } from '../../../../../common/components/new-stepper/step-data';
import { Supplier } from '../../../../../common/models/supplier';

export interface InvoiceDetailOverviewReducerState {
    stepData: StepData<OverviewFormValues>;
    isCredit: boolean;
    /**
     * The invoice as the server last handed it over (#2137).
     *
     * `stepData.values` cannot serve as the unsaved-changes baseline: leaving a
     * wizard step parks its input right there, so within a step change the two
     * become the same thing and nothing reads as unsaved any more. This is only
     * written when the server answers.
     */
    baseline?: OverviewFormValues;
}

export const initialInvoiceDetailOverviewReducerState: InvoiceDetailOverviewReducerState = {
    stepData: {
        errors: {},
        values: {
            status: DocumentStatus.Created,
            isCredit: false,
            poolBankInfo: null,
            invoiceDate: new Date(),
            shouldSendEmail: true,
            functionalAsset: null,
            costCentre: '',
            orderReference: '',
            noteToRecipient: '',
            internalNote: '',
            deliveryNumber: '',
            number: '',
            responsibleContact: null,
            recipientAccount: null,
            recipientContact: null,
            invoiceAddress: null,
            paymentTerm: null,
            supplierAccount: null,
            supplierContact: null,
        },
        isValid: false,
        isDirty: false,
    },
    isCredit: false,
};

export const invoiceDetailOverviewReducer = (
    state: InvoiceDetailOverviewReducerState = initialInvoiceDetailOverviewReducerState,
    action: AnyAction
): InvoiceDetailOverviewReducerState => {
    const { type } = action;

    switch (type) {
        case INVOICE_DETAIL_OVERVIEW_SUBMIT: {
            const data = action['data'] as StepData<OverviewFormValues>;
            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    ...data,
                },
            };
        }

        case INVOICE_DETAIL_OVERVIEW_RECEIVE: {
            const invoice = action['invoice'] as Invoice;
            const values: OverviewFormValues = {
                id: invoice.id,
                status: invoice.status,
                isCredit: invoice.isCredit,
                currency: invoice.currency,
                poolBankInfo: invoice.poolBankInfo,
                responsibleContact: invoice.responsibleContact,
                invoiceDate: invoice.invoiceDate,
                paymentTerm: invoice.paymentTerm,
                shouldSendEmail: invoice.shouldSendEmail ?? true,

                functionalAsset: invoice.functionalAsset,
                costCentre: invoice.costCentre,
                orderReference: invoice.orderReference,
                orderDate: invoice.orderDate,

                supplierAccount:
                    invoice.supplierAccount &&
                    Supplier.fromPlainObject({
                        ...invoice.supplierAccount,
                        paymentTermId: invoice.supplierPaymentTermId,
                        // The embedded account carries only the identifying
                        // fields; the terms travel beside it (#2184).
                        termsOfDelivery: invoice.supplierTermsOfDelivery,
                    }),
                supplierContact: invoice.supplierContact,
                deliveryNumber: invoice.deliveryNumber,
                deliveryDate: invoice.deliveryDate,
                incomingInvoiceNumber: invoice.incomingInvoiceNumber,

                recipientAccount:
                    invoice.recipientAccount &&
                    Account.fromPlainObject({
                        ...invoice.recipientAccount,
                        paymentTermId: invoice.recipientPaymentTermId,
                    }),
                recipientContact: invoice.recipientContact,
                invoiceAddress: invoice.invoiceAddress,
                recipientOrderContact: invoice.recipientOrderContact,

                noteToRecipient: invoice.noteToRecipient,
                internalNote: invoice.internalNote,

                number: invoice.number,
                classifications: invoice.classifications,
            };

            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    isValid: true,
                    values,
                },
                // The same values, kept apart from the ones the wizard edits.
                baseline: values,
                isCredit: invoice.isCredit,
            };
        }

        case INVOICE_DETAIL_POSITIONS_RECEIVE: {
            const invoice = action['invoice'] as Invoice;
            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    values: {
                        ...state.stepData.values,
                        status: invoice.status,
                    },
                },
                // The server has this status; carrying it only into the edited
                // values would make the mask report input nobody gave (#2210).
                baseline: state.baseline ? { ...state.baseline, status: invoice.status } : state.baseline,
            };
        }

        case INVOICE_DETAIL_RESET: {
            return initialInvoiceDetailOverviewReducerState;
        }

        case INVOICE_DETAIL_COMPLETE: {
            // Number and status come from the server, so they belong in the
            // comparison baseline too. Without that the mask counted its own
            // completion as unsaved input and the browser asked on the way out
            // (#2210, R-89).
            const completed = { number: action['number'] as string, status: DocumentStatus.Completed };

            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    values: { ...state.stepData.values, ...completed },
                },
                baseline: state.baseline ? { ...state.baseline, ...completed } : state.baseline,
            };
        }

        case INVOICE_DETAIL_CANCELED: {
            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    values: {
                        ...state.stepData.values,
                        status: DocumentStatus.Canceled,
                    },
                },
                baseline: state.baseline ? { ...state.baseline, status: DocumentStatus.Canceled } : state.baseline,
            };
        }

        case INVOICE_DETAIL_IS_CREDIT_CHANGED: {
            return {
                ...state,
                isCredit: !state.isCredit,
            };
        }

        default: {
            return state;
        }
    }
};
